import { DashboardProps, GlobalData } from "types/GlobalData";

export type DashboardGroup = GlobalData["dashboardGroups"][number];

// Persisted data can contain null or malformed entries because older drag writes
// could splice undefined into a group's dashboards array, and JSON serializes
// undefined entries as null.
export const isValidDashboard = (value: unknown): value is DashboardProps => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return typeof candidate.id === "string" && candidate.id.length > 0;
};

export const isValidDashboardGroup = (value: unknown): value is DashboardGroup => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return typeof candidate.id === "string" && candidate.id.length > 0;
};

export const getValidDashboards = (dashboards: unknown): DashboardProps[] => {
  if (!Array.isArray(dashboards)) {
    return [];
  }

  return dashboards.filter(isValidDashboard);
};

export const sanitizeDashboardGroups = (groups: unknown): DashboardGroup[] => {
  if (!Array.isArray(groups)) {
    return [];
  }

  return groups.filter(isValidDashboardGroup).map((group) => ({
    ...group,
    dashboards: getValidDashboards(group.dashboards),
  }));
};

// Raw persisted groups are preserved on load, so lookups must tolerate null slots
// instead of dereferencing group.id directly.
export const findDashboardGroupById = (
  groups: unknown,
  groupId: string,
): DashboardGroup | undefined => {
  if (!Array.isArray(groups)) {
    return undefined;
  }

  return groups.find((group) => isValidDashboardGroup(group) && group.id === groupId);
};

export interface ReorderResult {
  dashboards: DashboardProps[];
  changed: boolean;
}

export interface ActiveDashboardSelection {
  activeDashboardId?: string;
  activeDashboardGroupId?: string;
}

export interface DashboardGroupsLoadView {
  persistedGroups: DashboardGroup[];
  validGroups: DashboardGroup[];
}

// Load-time contract: keep the raw persisted slots for the next explicit write so
// simply opening the page never deletes invalid entries, and expose a sanitized
// view that lookups, widget sync and active selection can dereference safely.
// Persisted data can hold null slots, which the declared type cannot express, so
// the single cast below is confined to this deserialization boundary.
export const buildDashboardGroupsLoadView = (rawGroups: unknown): DashboardGroupsLoadView => ({
  persistedGroups: (Array.isArray(rawGroups) ? rawGroups.slice() : []) as DashboardGroup[],
  validGroups: sanitizeDashboardGroups(rawGroups),
});

// Resolves the active group/dashboard against the sanitized view so a persisted
// null entry cannot throw during hydration. Returns only the fields that need to
// change; an empty object means the current selection is already valid.
export const resolveActiveDashboardSelection = (
  current: Pick<GlobalData, "activeDashboardId" | "activeDashboardGroupId">,
  groups: DashboardGroup[],
): ActiveDashboardSelection => {
  const activeGroup = groups.find((group) => group.id === current.activeDashboardGroupId);

  if (activeGroup && activeGroup.dashboards.length > 0) {
    const isActiveInGroup = activeGroup.dashboards.some(
      (dashboard) => dashboard.id === current.activeDashboardId,
    );
    if (isActiveInGroup) {
      return {};
    }

    const owningGroup = groups.find((group) =>
      group.dashboards.some((dashboard) => dashboard.id === current.activeDashboardId),
    );
    if (owningGroup) {
      return { activeDashboardGroupId: owningGroup.id };
    }

    const lastDashboard = activeGroup.dashboards[activeGroup.dashboards.length - 1];
    return { activeDashboardId: lastDashboard.id };
  }

  // The active group is empty or gone. Prefer the first group that still holds a
  // valid dashboard so the selection never lands on a removed or null entry.
  const fallbackGroup = groups.find((group) => group.dashboards.length > 0);
  if (!fallbackGroup) {
    return {};
  }

  return {
    activeDashboardId: fallbackGroup.dashboards[0].id,
    activeDashboardGroupId: fallbackGroup.id,
  };
};

// Resolve both slides against the current validated list at drop time. This avoids
// stale drag indices and never writes invalid persisted entries back into state.
export const reorderDashboardsById = (
  dashboards: unknown,
  draggedId: string,
  targetId: string,
): ReorderResult => {
  const valid = getValidDashboards(dashboards);
  const droppedInvalid = valid.length !== (Array.isArray(dashboards) ? dashboards.length : 0);
  const dragIndex = valid.findIndex((dashboard) => dashboard.id === draggedId);
  const targetIndex = valid.findIndex((dashboard) => dashboard.id === targetId);

  if (dragIndex < 0 || targetIndex < 0) {
    return { dashboards: valid, changed: droppedInvalid };
  }

  if (dragIndex === targetIndex) {
    return { dashboards: valid, changed: droppedInvalid };
  }

  const next = [...valid];
  const [movedDashboard] = next.splice(dragIndex, 1);
  next.splice(targetIndex, 0, movedDashboard);

  return { dashboards: next, changed: true };
};
